import React from "react";
import { HintSurfaceContext, Kbd } from "../../../components/shortcuts/Kbd";
import { BenchDive } from "../../scenes/bench/BenchDive";
import { useGame, useShellVersion } from "../../useShell";
import { useBenchChrome } from "./useBenchDive";

/**
 * The one click that stands the player up from the bench: a tag at the
 * top centre of the view, above the bench top and clear of the rail
 * (left), the trays (bottom corners), the key hints (bottom centre), the
 * coach's cards (top left), and the top bar (top right), reading the
 * key that does the same. It is deliberately a labelled target rather
 * than "anywhere off the bench" — the dimmed shop around the pool of
 * light is busy with tools, trays, and a dragged piece springing back,
 * so a stray click out there must never stand the player up. It also
 * has to stay off the bench top itself: the engine reads mouse presses
 * off the canvas, so chrome lying over the top would swallow a drag's
 * first press.
 *
 * Escape backs out one layer at a time (a held tool goes back first);
 * the tag always stands up, whatever the hands hold — standing up puts
 * the tool back anyway (BenchDive.close).
 */
export const BenchStepBack: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const chrome = useBenchChrome();
  const dive = game.entities.tryGetSingleton(BenchDive);
  if (!dive || dive.openBenchKey === null) return null;

  return (
    <HintSurfaceContext.Provider value="chrome">
      <div
        className={`pointer-events-none absolute inset-x-0 top-6 z-50 flex justify-center ${chrome.className}`}
        inert={chrome.inert}
      >
        <button
          type="button"
          data-testid="bench-step-back"
          data-sfx="ui-back"
          aria-label="Step back from the bench"
          onClick={(event) => {
            event.stopPropagation();
            dive.close();
          }}
          className="hud-chip pointer-events-auto flex items-baseline gap-2 rounded px-3 py-2 font-condensed uppercase tracking-[0.15em] text-[0.7rem] text-paper-manila/80 hover:border-paper-manila/60 hover:text-paper-manila active:bg-paper-manila/10"
        >
          <Kbd>Esc</Kbd>
          step back
        </button>
      </div>
    </HintSurfaceContext.Provider>
  );
};
